import {
  CHANNEL_PING, CHANNEL_SUBPROTOCOL, type ChannelEnvelope, type ChannelServerMessage,
} from "@guandan/protocol";

/**
 * The player's connection to their own event stream.
 *
 * Everything the app used to poll for arrives here: invitations, queue
 * progress, a lobby filling up, a table being ready, rewards landing. The
 * socket is not authoritative — it is a delivery hint for rows already
 * committed in D1 — so this class does not have to guarantee delivery. What it
 * must do is keep an honest cursor, and admit when it is not connected.
 *
 * Two deliberate differences from `GameSocket`, which this otherwise mirrors:
 *
 *  - **No application-level ping.** The game socket sends one every ten
 *    seconds; doing that here would wake the player's hibernating Durable
 *    Object six times a minute, per tab, for nothing. The server registers a
 *    WebSocket auto-response pair instead, so the keepalive is answered by the
 *    runtime and the object stays asleep.
 *  - **Missed events are recovered, not detected.** Completeness comes from
 *    `/channel/sync` on connect and reconnect, never from inspecting the
 *    sequence for holes — see the note in the message handler for why holes are
 *    the normal case rather than a symptom.
 */

export type ConnectionState = "offline" | "connecting" | "live" | "recovering";

export interface ChannelHandlers {
  /** Committed events, in ascending `seq`, all newer than the cursor. */
  onEvents: (events: readonly ChannelEnvelope[]) => void;
  /** The socket is open and the server's log ends at `seq`. */
  onReady: (seq: number) => void;
  onState: (state: ConnectionState) => void;
}

/** Backoff, plus jitter so a server restart does not bring every client back at once. */
const BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 30_000];
const KEEPALIVE_MS = 45_000;

export class PlayerChannel {
  #ws: WebSocket | null = null;
  #attempt = 0;
  #closed = false;
  #reconnect = 0;
  #keepalive = 0;
  /** The highest `seq` durably scanned by `/channel/sync`. */
  #cursor = 0;

  constructor(private readonly handlers: ChannelHandlers) {}

  start(cursor: number): void {
    this.#closed = false;
    this.#cursor = cursor;
    this.#open();
  }

  /** Told by the owner after a successful `/channel/sync`, so both agree where we are. */
  setCursor(cursor: number): void {
    if (cursor > this.#cursor) this.#cursor = cursor;
  }

  get cursor(): number {
    return this.#cursor;
  }

  close(): void {
    this.#closed = true;
    window.clearTimeout(this.#reconnect);
    window.clearInterval(this.#keepalive);
    try { this.#ws?.close(1000, "signed out"); } catch { /* already gone */ }
    this.#ws = null;
    this.handlers.onState("offline");
  }

  #open(): void {
    if (this.#closed) return;
    this.handlers.onState(this.#attempt === 0 ? "connecting" : "recovering");
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${protocol}//${location.host}/api/channel/ws`, CHANNEL_SUBPROTOCOL);
    this.#ws = ws;

    ws.addEventListener("open", () => {
      this.#attempt = 0;
      // A frame every 45s keeps intermediaries from dropping an idle
      // connection. The server answers it from its auto-response pair, so it
      // never reaches the Durable Object's message handler.
      window.clearInterval(this.#keepalive);
      this.#keepalive = window.setInterval(() => {
        try { ws.send(CHANNEL_PING); } catch { /* the close handler will deal with it */ }
      }, KEEPALIVE_MS);
    });

    ws.addEventListener("message", (event) => {
      const raw = String(event.data);
      // The auto-response pong is a bare token, not JSON.
      if (!raw.startsWith("{")) return;
      let message: ChannelServerMessage;
      try { message = JSON.parse(raw) as ChannelServerMessage; } catch { return; }

      if (message.kind === "ready") {
        this.handlers.onState("live");
        this.handlers.onReady(message.seq);
        return;
      }
      if (message.kind !== "events" || message.events.length === 0) return;

      // In order, and only newer than the *durable sync* cursor. Live delivery
      // never advances that cursor: separate publish RPCs may reach the DO as
      // seq 101 and then seq 100. Advancing to 101 here would make both this
      // handler and `/sync?after=101` discard 100 forever.
      //
      // Note what is deliberately *not* checked here: contiguity. `seq` comes
      // from a table-wide AUTOINCREMENT, so one player's events are strictly
      // increasing but essentially never consecutive — the rows in between
      // belong to everybody else. Treating a jump as a gap would discard every
      // live event on a busy server. Completeness is guaranteed the other way
      // round instead: the `ready` frame reports where the server's log ends,
      // and every reconnect syncs from the cursor, so anything the socket
      // missed is read back from D1.
      const fresh = [...message.events]
        .sort((a, b) => a.seq - b.seq)
        .filter((envelope) => envelope.seq > this.#cursor);
      if (fresh.length === 0) return;

      this.handlers.onEvents(fresh);
    });

    const reopen = () => {
      window.clearInterval(this.#keepalive);
      if (this.#closed || this.#ws !== ws) return;
      this.#ws = null;
      this.handlers.onState("recovering");
      const delay = BACKOFF_MS[Math.min(this.#attempt, BACKOFF_MS.length - 1)]!;
      this.#attempt++;
      this.#reconnect = window.setTimeout(() => this.#open(), delay + Math.random() * 500);
    };
    ws.addEventListener("close", reopen);
    ws.addEventListener("error", reopen);
  }
}
